// The app stores money as a bare decimal in a single implicit currency (see
// backend db.UserRate). The *workspace* chooses a display currency (#3729),
// which is a label only — it never converts the stored amount. We render the
// amount with grouped thousands, the workspace's currency symbol, and cents only
// when the amount has them (NG-49) via `Intl.NumberFormat`. Components should
// read the code from the workspace store with `useMoneyFormatter()` (see
// ./use-money-formatter) rather than passing `currency` by hand; the bare
// functions below take an explicit code and fall back to USD so non-workspace
// call sites still format.
export const DEFAULT_CURRENCY = "USD";

// `Intl.NumberFormat` is comparatively expensive to construct, and these run per
// table cell, so memoize one formatter per currency and digit count.
// `narrowSymbol` renders the short symbol ($, £, €, ¥) rather than "US$"/"CA$";
// Intl falls back to the code itself for anything it doesn't recognize.
const formatterCache = new Map<string, Intl.NumberFormat>();

// Amounts show either no decimals or both cent digits — never one. "$1,234.5"
// isn't how money is written, so a half-cent-free amount rounds to the cent and
// then shows two digits or none.
type FractionDigits = 0 | 2;

function moneyFormatter(currency: string, fractionDigits: FractionDigits): Intl.NumberFormat {
  const key = `${currency || DEFAULT_CURRENCY}:${fractionDigits}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || DEFAULT_CURRENCY,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    } catch (error) {
      // An unexpected / malformed code (should never happen — the code comes
      // from the CurrencyOption-validated workspace setting) must not blow up a
      // render, so fall back to a plain grouped number.
      console.warn(`money: unsupported currency "${currency}"; formatting without a symbol`, error);
      formatter = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    }
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Decimal places for an amount: none unless it actually has cents (NG-49).
 *
 * Decided on the amount as it will be *rendered*, i.e. after rounding to the
 * cent — so 1234.001 is a whole 1234 and shows no decimals, while 1234.005 reads
 * as "1,234.01". Whole amounts are the common case in this product (rates,
 * estimates and the costs derived from them are round numbers), and "$1,234.00"
 * everywhere is noise that also implies a precision the estimate doesn't have.
 */
function fractionDigitsFor(value: number): FractionDigits {
  return Math.round(Math.abs(value) * 100) % 100 === 0 ? 0 : 2;
}

export function formatMoney(
  value: string | number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string {
  const num = typeof value === "string" ? Number.parseFloat(value) : value;
  if (num == null || !Number.isFinite(num)) return "—";
  return moneyFormatter(currency, fractionDigitsFor(num)).format(num);
}

// Signed money delta for a stat tile that sits beside plain `formatMoney` values
// (e.g. the project overview estimate variance, GH-3593). Always shows a sign
// with a real minus (U+2212) and otherwise formats exactly like the amounts it
// derives from, so a variance reads consistently next to them: 9300 → "+€9,300",
// -13000 → "−€13,000", -481.25 → "−€481.25", 0 → "€0".
export function formatSignedMoney(
  value: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (value === 0) return formatMoney(0, currency);
  return `${value > 0 ? "+" : "−"}${formatMoney(Math.abs(value), currency)}`;
}

// Compact, signed money delta for space-constrained labels (e.g. the per-project
// variance label on the estimated-vs-actual cost chart, GH-3487). Keeps the
// always-shown sign (real minus, U+2212) and k/M compact notation, now with the
// workspace currency symbol: 9300 → "+€9.3K", -13000 → "−€13K", 0 → "0",
// 500 → "+€500".
export function formatCompactMoneyDelta(
  value: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (value === 0) return "0";
  const sign = value > 0 ? "+" : "−";
  let compact: string;
  try {
    compact = Math.abs(value).toLocaleString(undefined, {
      style: "currency",
      currency: currency || DEFAULT_CURRENCY,
      currencyDisplay: "narrowSymbol",
      notation: "compact",
      // Stated, not inherited. Older ICU let compact notation drop the
      // currency's two decimals; ICU 78 keeps them up to the maximum, so the
      // same call read "+$12.0K" and "+$500.0" there (Node 22.22.2).
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  } catch (error) {
    console.warn(
      `money: unsupported currency "${currency}"; compact delta without a symbol`,
      error,
    );
    compact = Math.abs(value).toLocaleString(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    });
  }
  return `${sign}${compact}`;
}

// The bare symbol for a currency ("$", "£", "€"), with no amount attached —
// for the *input* affordance, where a field shows "USD $" ahead of the value
// the user types (#4223). Read surfaces don't need this: they format a real
// amount through `formatMoney`, which already carries the symbol.
//
// There is no Intl API that returns a symbol directly, so derive it the way the
// rest of this module derives everything: format a known amount and strip the
// number-ish characters back off. Falls back to the code itself when Intl has
// no narrow symbol (which is also what Intl does inside a formatted amount).
export function currencySymbol(currency: string = DEFAULT_CURRENCY): string {
  const code = currency || DEFAULT_CURRENCY;
  const symbol = moneyFormatter(code, 0)
    .format(0)
    .replace(/[\s \d.,]/g, "");
  return symbol || code;
}
